/* Hallmark · component: compact scoreboard · genre: playful · theme: existing Worldly */
/* Hallmark · pre-emit critique: P4 H5 E5 S5 R5 V4 */
/**
 * The dual ladder — the anxiety engine (spec 8.4).
 *
 * Your rungs on the left, theirs on the right, on the same baseline. Their
 * ladder lighting up while yours sits still is the core feeling of the game, so
 * an opponent transition animates immediately and their banked total is always
 * on screen. Being behind is what makes a -EV push correct; the player can only
 * do that maths if the number is in front of them.
 *
 * This component renders both seats from the same props. A ghost and a live
 * opponent reach it through the identical path (invariant 1) — there is no flag
 * here that could ever leak which one you are racing.
 */

import { memo } from 'react';
import type { OppPhase } from '@geogame/shared';
import type { MatchPhase } from '@geogame/rules/types';

import { points, rungProgress } from '../lib/format';

export type RungState = 'idle' | 'active' | 'cleared' | 'banked' | 'busted';

export interface LadderSideProps {
  readonly label: string;
  readonly rung: number;
  readonly depth: number;
  readonly total: number;
  readonly pot: number;
  readonly banked: boolean;
  readonly busted: boolean;
  readonly mirrored?: boolean;
  /** Changes on every transition, so the pip can replay its animation. */
  readonly seq: number;
  readonly you?: boolean;
  /**
   * Highest rung anyone OTHER than this ladder's subject has reached, so a
   * group match can show the field without a ladder per player.
   *
   * Rendered only where it EXCEEDS this ladder's own rung, which is what makes
   * it free in a duel: with one rival the field mark and the rival's rung are
   * the same number, the test fails, and nothing is drawn.
   */
  readonly fieldHighRung?: number;
  /**
   * CHANGE 6. This round is the golden one, so the ladder's TOP rung is where
   * the tripled multiplier lives. The brief asks for the golden round to be
   * unmistakable on "the ladder's top rung", and the ladder is the one piece of
   * HUD that is on screen in every phase — including the decision, which is the
   * moment the multiplier actually changes the maths.
   */
  readonly golden?: boolean;
}

function rungState(index: number, p: LadderSideProps): RungState {
  const rung = index + 1;
  if (p.banked) return rung <= p.rung ? 'banked' : 'idle';
  if (p.busted && rung === p.rung) return 'busted';
  if (rung < p.rung) return 'cleared';
  if (rung === p.rung) return 'active';
  return 'idle';
}

const LadderSide = memo(function LadderSide(props: LadderSideProps): React.JSX.Element {
  const rungs = Array.from({ length: props.depth }, (_, i) => i);
  return (
    <div
      className={`gg-ladder flex min-w-0 flex-1 flex-col ${
        props.mirrored ? 'items-end text-right' : 'items-start text-left'
      }`}
      data-you={props.you ? 'true' : 'false'}
      data-mirrored={props.mirrored ? 'true' : 'false'}
      aria-label={`${props.label}: ${points(props.total)} points, ${rungProgress(props.rung, props.depth)}`}
    >
      <div className="gg-ladder-head">
        <span className="gg-ladder-name">{props.label}</span>
        <span className="gg-ladder-scoreline">
          <span className="gg-ladder-total tabular-nums">{points(props.total)}</span>
          {props.pot > 0 && !props.banked ? (
            <span className="gg-ladder-pot tabular-nums">pot {points(props.pot)}</span>
          ) : null}
        </span>
      </div>

      {/* The same climb, compressed left-to-right into one glanceable track. */}
      <div className="gg-rung-track" aria-hidden="true">
        {rungs.map((i) => {
          const state = rungState(i, props);
          // The field's high-water mark, and only when someone out there is
          // genuinely ahead of the player this ladder names.
          const field = (props.fieldHighRung ?? 0) > props.rung && i + 1 === props.fieldHighRung;
          return (
            <span
              key={i}
              className="gg-rung-dot"
              data-state={state}
              data-field={field ? 'true' : 'false'}
              data-golden={props.golden === true && i + 1 === props.depth ? 'true' : 'false'}
              data-seq={state === 'active' || state === 'banked' ? props.seq : 0}
            />
          );
        })}
      </div>
    </div>
  );
});

export interface DualLadderProps {
  readonly depth: number;
  /** Both seats hit the golden round on the same index (change 6), so one flag. */
  readonly golden?: boolean;
  readonly you: {
    readonly handle: string;
    readonly rung: number;
    readonly total: number;
    readonly pot: number;
    readonly phase: MatchPhase;
    readonly banked: boolean;
    readonly busted: boolean;
  };
  readonly opponent: {
    readonly handle: string;
    readonly rung: number;
    readonly total: number;
    readonly phase: OppPhase;
    readonly banked: boolean;
    readonly seq: number;
    /** Group matches only; a duel leaves this equal to `rung` and it is hidden. */
    readonly fieldHighRung?: number;
  };
}

export function DualLadder({
  depth,
  you,
  opponent,
  golden = false,
}: DualLadderProps): React.JSX.Element {
  return (
    <div className="gg-dual pointer-events-none flex w-full items-center justify-between">
      <LadderSide
        label={you.handle}
        rung={you.rung}
        depth={depth}
        total={you.total}
        pot={you.pot}
        banked={you.banked}
        busted={you.busted}
        seq={you.rung}
        golden={golden}
        you
      />

      <div className="gg-versus flex shrink-0 flex-col items-center opacity-70">
        <span className="text-[10px] font-semibold uppercase tracking-[0.2em]">vs</span>
        <span className="gg-diff tabular-nums text-xs font-semibold">
          {you.total === opponent.total
            ? 'level'
            : you.total > opponent.total
              ? `+${points(you.total - opponent.total)}`
              : `-${points(opponent.total - you.total)}`}
        </span>
      </div>

      <LadderSide
        label={opponent.handle}
        rung={opponent.rung}
        depth={depth}
        total={opponent.total}
        pot={0}
        banked={opponent.banked}
        busted={opponent.phase === 'busted'}
        seq={opponent.seq}
        golden={golden}
        fieldHighRung={opponent.fieldHighRung ?? 0}
        mirrored
      />
    </div>
  );
}
